import PlusSignIcon from 'components/icons/PlusSignIcon';
import Tooltip from 'components/Tooltip';
import Image from 'next/image';
import InlineTooltip from 'components/InlineTooltip';
import HotspotTooltip from 'components/HotspotTooltip';
const cardImages = [
  {
    src: '/plan-g-timeline-hospital.svg',
    alt: 'Medicare Part A and Plan G hospital costs chart',
    caption:
      'Infographic shows how hospital costs are covered with Medicare Part A and Medicare Supplement Plan G. From Days 1 through 60, Plan G covers the full cost of the Part A deductible ($1,736). From Days 61 to 90, Plan G pays the $434 per day copay, for a total of $13,020 for 30 days. From Days 91 to 150, Plan G pays the $868 per day copay, for a total of $52,080 for 60 days. Plan G adds an extra 365 Lifetime Reserve Days before coverage ends.',
    width: 1060,
    height: 785,
  },
  {
    src: '/plan-g-donut-step-1.svg',
    alt: 'Medicare Part A and Plan G specialist visits costs chart',
    caption:
      "Infographic showing the cost of a visit to an orthopedic specialist for a pain in Sarah's wrist with Medicare Part B and Medicare Supplement Plan G. The total cost for the visit before insurance is $502. Sarah first pays a $283 deductible. After the deductible, $219 remains. Plan G pays 20% coinsurance of that amount ($44). Medicare Part B pays the remaining 80% coinsurance ($175).",
    width: 1060,
    height: 597,
  },
  {
    src: '/plan-g-donut-step-2.svg',
    alt: 'Medicare Part A and Plan G MRI costs chart',
    caption:
      "Infographic showing the cost of an MRI for Sarah's wrist with Medicare Part B and Medicare Supplement Plan G. The total cost of the MRI before insurance is $3,000. Plan G pays 20% coinsurance ($600). Medicare Part B pays the remaining 80% coinsurance ($2,400).",
    width: 1060,
    height: 527,
  },
  {
    src: '/plan-g-donut-step-3.svg',
    alt: 'Medicare Part A and Plan G outpatient surgery costs chart',
    caption:
      "Infographic showing the cost of outpatient surgery to treat Sarah's wrist with Medicare Part B and Medicare Supplement Plan G. The total cost of the surgery before insurance is $5,750. Medicare Part B pays for 80% coinsurance ($4,000). Plan G pays the 15% excess charge that is not covered by Medicare ($750). Plan G also pays 20% coinsurance on the remainder ($1,000)",
    width: 1060,
    height: 597,
  },
  {
    src: '/part-d-rx-calendar.svg',
    alt: 'Medicare Part D and Plan G rx costs chart',
    caption:
      "Infographic showing the cost of prescription drugs with Medicare Part D and Medicare Supplement Plan G. Daniel is prescribed a Tier 1 (generic) drug that costs $24, before insurance, and 2 Tier 3 (brand-name) drug that cost $890 in total, before insurance. In January, Daniel pays a copay ($10) and a deductible ($615). The amount remaining is $275. Daniel pays 20% coinsurance ($55) and the plan covers the remaining 80% coinsurance ($220). In January, Daniel pays $680 in total. From February through August, Daniel pays a copay ($10) and 20% coinsurance ($178) and the plan covers the remaining 80% coinsurance ($712). For these months, he's paying $188 each month in total. In September, Daniel pays a copay ($10). He reaches his out-of-pocket maximum ($2,100) so he only pays $104 in coinsurance. The plan pays 100% for the rest of the year and Daniel doesn't pay anything.",
    width: 1060,
    height: 1315,
  },
];

const WhatsSupp = () => {
  return (
    <section className="lfe-page-container coverage-explainer med-supp">
      {/* Header */}
      <div className="coverage-explainer-header">
        <h2>
          Medicare Supplement Plan G and Part D Coverage, Added to Original
          Medicare (Parts A and B){' '}
        </h2>
        <div className="versus-circle">
          <h2>vs</h2>
        </div>
        <h2>Original Medicare (Parts A and B) Alone</h2>
        <div className="subtitle">
          <>
            <p>
              The following is based on the plan details from the examples of
              Parts A, B, above, combined with representative Part D coverage
              and a Medicare Supplement Plan G. (Plan G is one of the most
              popular Medicare Supplement plans, nationwide, according to both{' '}
              <a href="https://www.medicaresupplement.com/" target="_blank">
                MedicareSupplement.com
              </a>{' '}
              and{' '}
              <a
                href="https://boomerbenefits.com/medicare-supplemental-insurance/medicare-supplement-plans/medicare-plan-g/"
                target="_blank"
              >
                BoomerBenefits.com
              </a>
              )
            </p>
            <p>
              Cost for Medicare Supplement Plan G is a national average
              estimated by{' '}
              <a href="https://www.medicaresupplement.com/" target="_blank">
                MedicareSupplement.com
              </a>
              . Costs for Part D (the deductible maximum and the out-of-pocket
              maximum) are specific limits set by CMS, while the costs per tier
              are a national average estimated by{' '}
              <a
                href="https://www.ncoa.org/article/how-much-does-medicare-part-d-cost/"
                target="_blank"
              >
                The National Council on Aging
              </a>
              . The costs presented here for Part D do not represent an actual
              plan, but rather generalized features you might find in a plan.
              Actual plan premiums, costs, and details, as well as the costs for
              provider visits and services, may vary in your area.
            </p>
          </>
        </div>
      </div>

      {/* Coverage details card */}
      <div className="lfe-card coverage-card">
        <div className="lfe-card-header centered">
          <h3>Coverage Details</h3>
        </div>
        <div className="lfe-card-content">
          <div className="card-col-container">
            {/* Left column */}
            <div className="card-col">
              {/* Med supp card */}
              <div className="itemized-costs-card med-supp">
                <div className="itemized-costs-content">
                  <table>
                    <caption>Medicare Supplement Plan G</caption>
                    <tbody>
                      <tr>
                        <th scope="row">
                          Monthly{' '}
                          <InlineTooltip
                            text="Premium"
                            tooltipText={'premium'}
                          />
                          :
                        </th>
                        <td>
                          <b>{'$165.85'}</b>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Plus sign */}
              <div className="plus-sign">
                <PlusSignIcon size="25" color="blue" />
              </div>

              {/* Part D card */}
              <div className="itemized-costs-card part-d">
                <div className="itemized-costs-content">
                  <table>
                    <caption>Part D</caption>
                    <tbody>
                      <tr>
                        <th scope="row">
                          Monthly{' '}
                          <InlineTooltip
                            text="Premium"
                            tooltipText={'premium'}
                          />
                          :
                        </th>
                        <td>
                          <b>{'$34.50'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="rowgroup">
                          <InlineTooltip
                            text="Deductible"
                            tooltipText={'deductible'}
                          />
                        </th>
                        <td>
                          Rx{' '}
                          <InlineTooltip text="Tiers" tooltipText={'rxTiers'} />{' '}
                          1 and 2: <b>None</b>
                          <br></br>
                          Rx{' '}
                          <InlineTooltip
                            text="Tiers"
                            tooltipText={'rxTiers'}
                          />{' '}
                          3, 4, and 5: <b>$615</b>
                          <Tooltip
                            horizontalAlign="right"
                            color="blue"
                            subject="Rx tiers"
                          >
                            Covered prescriptions are divided up into groups, or
                            tiers, on a list called a formulary. Different tiers
                            may have different levels of coverage. A plan&apos;s
                            Summary of Benefits will offer more detail on the
                            plan&apos;s formulary.
                          </Tooltip>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip text="Copays" tooltipText={'copay'} />
                        </th>
                        <td>
                          Rx Tiers 1 and 2: <b>{'$5-$25'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Coinsurance"
                            tooltipText={'coinsurance'}
                          />
                        </th>
                        <td>
                          Rx Tiers 3, 4, and 5: <b>{'25%'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Out-of-Pocket Max"
                            tooltipText={'oopm'}
                          />
                        </th>
                        <td>
                          <b>$2,100</b>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Plus sign column */}
            <div className="plus-sign">
              <PlusSignIcon size="25" color="blue" />
            </div>

            {/* Right column */}
            <div className="card-col">
              {/* Part A and B card */}
              <div className="itemized-costs-card original-medicare">
                <div className="itemized-costs-content">
                  <table>
                    <caption>Original Medicare</caption>
                    <tbody>
                      <tr className="section-header">
                        <th scope="colgroup" colSpan={2}>
                          Part A
                        </th>
                      </tr>
                      <tr>
                        <th scope="row">
                          Monthly{' '}
                          <InlineTooltip
                            text="Premium"
                            tooltipText={'premium'}
                          />{' '}
                          (if any):
                        </th>
                        <td>
                          <b>Covered</b> by Medicare Supplement Plan G
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Deductible"
                            tooltipText={'deductible'}
                          />
                          :
                        </th>
                        <td>
                          <b>Covered</b> by Medicare Supplement Plan G
                        </td>
                      </tr>
                      <tr className="section-header part-b">
                        <th scope="colgroup" colSpan={2}>
                          Part B
                        </th>
                      </tr>
                      <tr>
                        <th scope="row">
                          Monthly{' '}
                          <InlineTooltip
                            text="Premium"
                            tooltipText={'premium'}
                          />
                          :
                        </th>
                        <td>
                          <b>$202.90</b> (minimum)
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Deductible"
                            tooltipText={'deductible'}
                          />
                          :
                        </th>
                        <td>
                          <b>$283</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Coinsurance"
                            tooltipText={'coinsurance'}
                          />
                          :
                        </th>
                        <td>
                          <b>Covered</b> by Medicare Supplement Plan G
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example card - Hospital */}
      <div className="lfe-card example-card">
        <div className="lfe-card-header">
          <h3 className="centered">
            Example of How Medicare Supplement Plan G and Part D Coverage, Added
            to Original Medicare, Could Apply to Daniel&rsquo;s Hospital Stay
            (in 2026)
          </h3>
        </div>

        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <p className="example-content">
              Daniel is admitted to the hospital for cancer treatment. His
              treatment requires hospitalization for over 150 days.
            </p>
          </div>

          {/* Timeline Chart */}
          <figure className="annotated-image">
            <Image
              src={cardImages[0].src}
              alt={cardImages[0].alt}
              width={cardImages[0].width}
              height={cardImages[0].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[0].caption}
            </figcaption>
            <HotspotTooltip
              x={19}
              y={39}
              text="deductible"
              tooltipText="deductible"
            />
            <HotspotTooltip x={35} y={32} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={69}
              y={29}
              text="copay"
              tooltipText="copay"
              direction="left"
            />
          </figure>

          <div className="itemized-costs-container">
            <div className="itemized-container-header">
              <h3>Example Total Costs for Hospital Stay</h3>
            </div>

            <div className="card-col-container">
              {/* Left column */}
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>Medicare Supplement Plan G and Part D Coverage,</h4>
                  <p>added to Original Medicare (Parts A and B)</p>
                </div>

                {/* Med Supp card */}
                <div className="itemized-costs-card med-supp">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Medicare Supplement Plan G</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$1,990'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part D card */}
                <div className="itemized-costs-card part-d">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Part D</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$414'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip text="Copay" tooltipText={'copay'} />{' '}
                            Total for Days 61-150:
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>Overall Total for 150 days:</p>
                    <p className="total-number">{'$4,839'}</p>
                  </div>
                  <p>
                    (then coverage continues 365 days at $0/day before lifetime
                    reserve days are used up and coverage ends)
                  </p>
                </div>
              </div>

              {/* Versus */}
              <div className="versus-circle">
                <h3>vs</h3>
              </div>

              {/* Right column */}
              <div className="card-col">
                {/* Header*/}
                <div className="card-col-header">
                  <h4>
                    Original Medicare<br></br>Parts A and B (Alone)
                  </h4>
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$1,736</b> per benefit period
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip text="Copay" tooltipText={'copay'} />{' '}
                            Total for Days 61-150:{' '}
                          </th>
                          <td>
                            <b>$65,100</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>Overall Total for 150 days:</p>
                    <p className="total-number">{'$69,271'}</p>
                  </div>
                  <p>{'(then coverage ends)'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example card - Medical Services */}
      <div className="lfe-card example-card">
        <div className="lfe-card-header">
          <h3 className="centered">
            Example of How Medicare Supplement Plan G and Part D Coverage, Added
            to Original Medicare, Could Apply to Sarah&rsquo;s Medical Services
            (in 2026)
          </h3>
        </div>
        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <ol className="example-content small">
              <li>Sarah visits her orthopedist for a pain in her wrist.</li>
              <li>
                {
                  'The doctor orders an MRI, confirming she needs a total shoulder replacement.'
                }
              </li>
              <li>An outpatient surgery is performed as treatment.</li>
            </ol>
          </div>

          {/* Donut Charts */}
          <figure className="annotated-image">
            <Image
              src={cardImages[1].src}
              alt={cardImages[1].alt}
              width={cardImages[1].width}
              height={cardImages[1].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[1].caption}
            </figcaption>
            <HotspotTooltip
              x={34}
              y={22}
              text="deductible"
              tooltipText="deductible"
            />
            <HotspotTooltip
              x={59}
              y={53}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={60}
              y={78}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={58}
              y={84}
              text="deductible"
              tooltipText="deductible"
              direction="left"
            />
          </figure>
          <figure className="annotated-image">
            <Image
              src={cardImages[2].src}
              alt={cardImages[2].alt}
              width={cardImages[2].width}
              height={cardImages[2].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[2].caption}
            </figcaption>
            <HotspotTooltip
              x={55}
              y={37}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={64}
              y={70}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip
              x={56}
              y={76}
              text="deductible"
              tooltipText="deductible"
              direction="left"
            />
          </figure>
          <figure className="annotated-image">
            <Image
              src={cardImages[3].src}
              alt={cardImages[3].alt}
              width={cardImages[3].width}
              height={cardImages[3].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[3].caption}
            </figcaption>
            <HotspotTooltip
              x={66}
              y={22}
              text="coinsurance"
              tooltipText="coinsurance"
            />
            <HotspotTooltip
              x={55}
              y={46}
              text="15% excess charge"
              tooltipText="excessCharge"
              direction="left"
            />
            <HotspotTooltip
              x={64}
              y={76}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
          </figure>

          <div className="itemized-costs-container">
            <div className="itemized-container-header">
              <h3>Example Total Costs for Medical Services</h3>
            </div>

            <div className="card-col-container">
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>Medicare Supplement Plan G and Part D Coverage,</h4>
                  <p>added to Original Medicare (Parts A and B)</p>
                </div>

                {/* Med Supp card */}
                <div className="itemized-costs-card med-supp">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Medicare Supplement Plan G</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$1,990'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part D card */}
                <div className="itemized-costs-card part-d">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Part D</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$414'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$283</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Coinsurance"
                              tooltipText={'coinsurance'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="15% Excess Charge"
                              tooltipText={'excessCharge'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>{'Overall Total:'}</p>
                    <p className="total-number">{'$5,122'}</p>
                  </div>
                </div>
              </div>
              <div className="versus-circle">
                <h3>vs</h3>
              </div>
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>
                    Original Medicare<br></br>Parts A and B (Alone)
                  </h4>
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$283</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Coinsurance"
                              tooltipText={'coinsurance'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>{'$2,847'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="15% Excess Charge"
                              tooltipText={'excessCharge'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>{'$2,394'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>{'Overall Total:'}</p>
                    <p className="total-number">{'$7,959'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example card - Prescriptions */}
      <div className="lfe-card example-card">
        <div className="lfe-card-header">
          <h3 className="centered">
            Example of How Medicare Supplement Plan G and Part D Coverage, Added
            to Original Medicare, Could Apply to Daniel&rsquo;s Prescription
            Needs (in 2026)
          </h3>
        </div>

        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <div className="example-content">
              <p>Daniel is prescribed 3 different medications:</p>
              <ul className="example-content">
                <li>
                  <b>1</b>{' '}
                  <InlineTooltip text="Tier 1" tooltipText={'rxTiers'} />{' '}
                  Generic Drug
                </li>
                <li>
                  <b>2</b>
                  <InlineTooltip text="Tier 5" tooltipText={'rxTiers'} />{' '}
                  Brand-Name Drugs
                </li>
              </ul>
            </div>
          </div>

          {/* Pills chart */}
          <figure className="annotated-image">
            <Image
              src={cardImages[4].src}
              alt={cardImages[4].alt}
              width={cardImages[4].width}
              height={cardImages[4].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[4].caption}
            </figcaption>
            <HotspotTooltip x={24} y={29} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={37}
              y={30}
              text="deductible"
              tooltipText="deductible"
            />
            <HotspotTooltip
              x={60}
              y={23}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip x={24} y={49} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={52}
              y={43}
              text="coinsurance"
              tooltipText="coinsurance"
            />
            <HotspotTooltip x={24} y={69} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={52}
              y={64}
              text="coinsurance"
              tooltipText="coinsurance"
            />

            <HotspotTooltip
              x={50}
              y={71}
              text="out-of-pocket max"
              tooltipText="oopm"
            />
          </figure>

          <div className="itemized-costs-container">
            <div className="itemized-container-header">
              <h3>Example Total Costs for Prescription Coverage</h3>
            </div>

            <div className="card-col-container">
              <div className="card-col">
                {/* Header  */}
                <div className="card-col-header">
                  <h4>Medicare Supplement Plan G and Part D Coverage,</h4>
                  <p>added to Original Medicare (Parts A and B)</p>
                </div>

                {/* Med Supp card */}
                <div className="itemized-costs-card med-supp">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Medicare Supplement Plan G</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$1,990'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part D card */}
                <div className="itemized-costs-card part-d">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Part D</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$414'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$615</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Copays"
                              tooltipText={'copay'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>$90</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Coinsurance"
                              tooltipText={'coinsurance'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>{'$1,518'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>Overall Total:</p>
                    <p className="total-number">{'$7,062'}</p>
                  </div>
                </div>
              </div>
              <div className="versus-circle">
                <h3>vs</h3>
              </div>
              <div className="card-col">
                {/* Header  */}
                <div className="card-col-header">
                  <h4>
                    Original Medicare<br></br>Parts A and B (Alone)
                  </h4>
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Yearly Uninsured Rx Total:</th>
                          <td>
                            <b>$10,968</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>Overall Total:</p>
                    <p className="total-number">{'$13,403'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatsSupp;
